import { useState, ReactNode, useEffect } from "react"
import { useDispatch } from "react-redux"
import { NotificationHub } from "@nef/core"
import { useHistory } from "react-router-dom"

import { ACTIVITY_LOGOUT_TIME, REFRESH_TOKEN_TIME } from "./constants/settings"
import AuthContext from "./contexts/AuthContext"
import AuthTimeoutModal from "./app_pages/app_components/app_widgets/modals/AuthTimeoutModal"
import { AppDispatch } from "./app/store"
import { logoutUser, refreshUserToken } from "./redux/auth/actions"
import setAccessTokenLocalStorage from "./utils/authetication"


interface AuthProviderProps {
  children?: ReactNode
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [accessToken, setAccessToken] = useState<string | null>(localStorage.getItem("nfn-access-token"))
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const dispatch = useDispatch<AppDispatch>()
  const history = useHistory()

  const isTokenValid = (token: string) => {
    if (!token || token.length < 270) return false
    const parsedToken = JSON.parse(atob(token.split(".")[1]))
    if (!parsedToken.exp) return false
    return Date.now() < parsedToken.exp * 1000
  }

  const refreshToken = async () => {
    try {
      const res = await dispatch(refreshUserToken()).unwrap()
      if (!res.data) return
      setAccessToken(res.data)
      setAccessTokenLocalStorage(res.data)
    } catch (error: any) {
      NotificationHub.send("danger", `${error.status} ${error.message}`)
      handleLogOutUser(false)
    }
  }

  // utility to logout user
  const handleLogOutUser = async (invalidateToken = true) => {
    // * donot call logout api if a valid token isn't present already
    try {
      if (invalidateToken && accessToken) await dispatch(logoutUser()).unwrap()
    } catch (error) {
      NotificationHub.send("danger", "Something went wrong!")
    } finally {
      // move the user to home page
      history.push("/")
      localStorage.clear()
      setAccessToken(null)
    }
  }

  useEffect(() => {
    if (!accessToken) return undefined

    const activityEvents = ["click", "keypress", "scroll", "mousemove", "touchstart"]
    let logOutHandle: null | ReturnType<typeof setTimeout> = null

    const logOutReset = () => {
      localStorage.setItem("lastActivity", Date.now().toString())
      if (logOutHandle) {
        clearTimeout(logOutHandle)
      }
      logOutHandle = setTimeout(() => {
        activityEvents.forEach((e) => window.removeEventListener(e, logOutReset))
        handleLogOutUser()
        setIsModalOpen(true)
        localStorage.removeItem("_routemap")
      }, ACTIVITY_LOGOUT_TIME)
    }

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === "lastActivity") {
        logOutReset()
      }
    }

    logOutReset()
    activityEvents.forEach((e) => window.addEventListener(e, logOutReset))
    window.addEventListener("storage", handleStorageEvent)

    return () => {
      activityEvents.forEach((e) => window.removeEventListener(e, logOutReset))
      window.removeEventListener("storage", handleStorageEvent)
      if (logOutHandle) {
        clearTimeout(logOutHandle)
      }
    }
  }, [accessToken])

  useEffect(() => {
    if (!accessToken) return undefined

    if (!isTokenValid(accessToken)) {
      handleLogOutUser()
      return undefined
    }

    const refreshTokenInterval = setInterval(() => {
      refreshToken()
    }, REFRESH_TOKEN_TIME)

    return () => {
      clearInterval(refreshTokenInterval)
    }
  }, [accessToken])

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        setAccessToken,
        handleLogOutUser,
      }}
    >
      {children}
      <AuthTimeoutModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
    </AuthContext.Provider>
  )
}

export default AuthProvider
